import React, { useState, useEffect } from 'react';
import { Save, Trash2, RefreshCw, Check, Settings, ArrowLeft } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { candidatesAPI } from "../../services/api";
import { toast } from "react-toastify";

const FieldConfiguration = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [availableFields, setAvailableFields] = useState([]);
  const [selectedFields, setSelectedFields] = useState([]);
  const [configExists, setConfigExists] = useState(false);
  const [initializing, setInitializing] = useState(true);

  // Get organization ID from localStorage and initialize
  useEffect(() => {
    initializeModal();
  }, []);

  // Initialize page
  const initializeModal = async () => {
    setInitializing(true);
    try {
      // Check if configuration exists
      const existsResponse = await candidatesAPI.checkConfigExists();
      const exists = existsResponse.data === true || existsResponse.data?.exists === true;
      setConfigExists(exists);

      // Fetch available fields
      const availRes = await candidatesAPI.getAvailableFields();
      const fields = Array.isArray(availRes.data) ? availRes.data : [];
      const EXCLUDED_FIELDS = [
        "id",
        "organizationId",
        "createdAt",
        "updatedAt",
        "lastActivity",
        "jobId",
        "status",
        "sourceOfApplication"
      ];
      const filtered = fields.filter(f => !EXCLUDED_FIELDS.includes(f));
      setAvailableFields(filtered);

      // If config exists, fetch configured fields
      if (exists) {
        const configRes = await candidatesAPI.getFields();
        const configFields = Array.isArray(configRes.data) ? configRes.data : [];
        setSelectedFields(configFields);
      } else if (filtered.length > 0) {
        // Default to all fields if no config exists
        setSelectedFields(filtered);
      }
    } catch (error) {
      console.error('Error initializing configuration:', error);
      toast.error('Failed to load field configuration');
    } finally {
      setInitializing(false);
    }
  };

  // Handle field selection toggle
  const handleFieldToggle = (field) => {
    setSelectedFields(prev => {
      const isSelected = prev.includes(field);
      if (isSelected) {
        return prev.filter(f => f !== field);
      } else {
        return [...prev, field];
      }
    });
  };

  // Handle select all / deselect all
  const handleSelectAll = () => {
    if (selectedFields.length === availableFields.length) {
      setSelectedFields([]);
    } else {
      setSelectedFields([...availableFields]);
    }
  };

  const handleSave = async () => {
    if (selectedFields.length === 0) {
      toast.warning('Please select at least one field');
      return;
    }

    setLoading(true);
    try {
      const payload = { selectedFields: selectedFields };
      const response = await candidatesAPI.saveOrUpdateFieldConfig(payload);
      
      if (response.status === 200 || response.status === 201) {
        toast.success(configExists ? 'Configuration updated' : 'Configuration created');
        setConfigExists(true);
      } else {
        throw new Error(`Unexpected status: ${response.status}`);
      }
    } catch (error) {
      console.error('Error saving config:', error);
      toast.error(error.response?.data?.message || 'Failed to save configuration');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('Reset to default fields? This will delete your custom configuration.')) return;

    setLoading(true);
    try {
      await candidatesAPI.deleteFieldConfig();
      toast.success('Configuration reset to default');
      setConfigExists(false);
      setSelectedFields(availableFields);
    } catch (error) {
      toast.error('Failed to reset configuration');
    } finally {
      setLoading(false);
    }
  };

  const formatFieldName = (fieldName) => {
    return fieldName
      .replace(/([A-Z])/g, ' $1')
      .replace(/^./, str => str.toUpperCase())
      .trim();
  };

  const getFieldCategory = (field) => {
    const categories = {
      'Basic Info': ['firstName', 'lastName', 'primaryEmail', 'primaryPhone', 'dateOfBirth', 'gender'],
      'Professional': ['currentJobTitle', 'currentCompany', 'totalYearsOfExperience', 'noticePeriod'],
      'Compensation': ['currentCTC', 'expectedCTC', 'salaryCurrency'],
      'Education': ['highestEducationLevel', 'fieldOfStudy', 'universityName', 'gpa'],
      'Location': ['city', 'state', 'country', 'currentLocation', 'preferredLocations'],
      'Skills': ['technicalSkills', 'softSkills', 'languages'],
      'Documents': ['resumeUrl', 'coverLetterUrl', 'profilePhotoUrl'],
      'Contact': ['whatsappNumber', 'linkedInProfile', 'githubProfile', 'portfolioWebsite'],
      'Status': ['status', 'jobId', 'sourceOfApplication', 'createdAt']
    };
    for (const [category, fields] of Object.entries(categories)) {
      if (fields.includes(field)) return category;
    }
    return 'Other';
  };

  const groupedFields = availableFields.reduce((groups, field) => {
    const category = getFieldCategory(field);
    if (!groups[category]) groups[category] = [];
    groups[category].push(field);
    return groups;
  }, {});

  const sortedCategories = Object.keys(groupedFields).sort();

  return (
    <div className="p-6 max-w-4xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div className="flex items-center gap-4">
          <button 
            onClick={() => navigate(-1)}
            className="p-2 hover:bg-gray-100 rounded-full transition-colors"
          >
            <ArrowLeft size={20} className="text-gray-600" />
          </button>
          <div>
            <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
              <Settings className="text-blue-600" size={24} />
              Field Configuration
            </h1>
            <p className="text-sm text-gray-500 mt-1">
              Configure which fields are visible and required in candidate forms.
            </p>
          </div>
        </div>
        <div className="flex gap-3">
          {configExists && (
            <button
              onClick={handleDelete}
              disabled={loading || initializing}
              className="px-4 py-2 text-sm font-medium text-red-600 bg-white border border-red-200 rounded-lg hover:bg-red-50 disabled:opacity-50 flex items-center gap-2"
            >
              <Trash2 size={16} />
              Reset to Default
            </button>
          )}
          <button
            onClick={handleSave}
            disabled={loading || initializing || selectedFields.length === 0}
            className="px-6 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-50 flex items-center gap-2 shadow-sm"
          >
            <Save size={16} />
            {loading ? 'Saving...' : 'Save Changes'}
          </button>
        </div>
      </div>

      {initializing ? (
        <div className="flex flex-col items-center justify-center py-20 bg-white rounded-2xl border border-dashed border-gray-200">
          <RefreshCw className="text-blue-600 animate-spin mb-4" size={32} />
          <p className="text-gray-500">Loading your configuration...</p>
        </div>
      ) : (
        <div className="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden">
          {/* Status and Global Actions */}
          <div className="px-6 py-4 bg-gray-50 border-b flex items-center justify-between">
            <div className="flex items-center gap-3">
              {configExists ? (
                <div className="flex items-center gap-2 text-green-700 bg-green-50 px-3 py-1 rounded-full text-xs font-semibold border border-green-100">
                  <Check size={14} /> Custom Configuration Active
                </div>
              ) : (
                <div className="flex items-center gap-2 text-amber-700 bg-amber-50 px-3 py-1 rounded-full text-xs font-semibold border border-amber-100">
                  <div className="w-1.5 h-1.5 bg-amber-500 rounded-full animate-pulse" /> Using Default Settings
                </div>
              )}
              <span className="text-xs text-gray-400">
                {selectedFields.length} of {availableFields.length} fields selected
              </span>
            </div>
            <button
              onClick={handleSelectAll}
              className="text-sm text-blue-600 hover:text-blue-700 font-medium"
            >
              {selectedFields.length === availableFields.length ? 'Deselect All' : 'Select All'}
            </button>
          </div>

          {/* Categories Grid */}
          <div className="p-6 grid grid-cols-1 md:grid-cols-2 gap-8">
            {sortedCategories.map(category => (
              <div key={category} className="space-y-3">
                <h3 className="text-sm font-bold text-gray-400 uppercase tracking-wider">
                  {category}
                </h3>
                <div className="space-y-1.5">
                  {groupedFields[category].map(field => (
                    <div
                      key={field}
                      onClick={() => handleFieldToggle(field)}
                      className={`flex items-center p-3 rounded-xl border transition-all cursor-pointer ${
                        selectedFields.includes(field)
                          ? 'bg-blue-50 border-blue-200 text-blue-900 shadow-sm'
                          : 'bg-white border-gray-100 text-gray-600 hover:border-gray-300'
                      }`}
                    >
                      <div className={`w-5 h-5 rounded-md border flex items-center justify-center mr-3 transition-colors ${
                        selectedFields.includes(field)
                          ? 'bg-blue-600 border-blue-600'
                          : 'bg-white border-gray-300'
                      }`}>
                        {selectedFields.includes(field) && <Check size={14} className="text-white" />}
                      </div>
                      <span className="text-sm font-medium flex-1">
                        {formatFieldName(field)}
                      </span>
                      {['firstName', 'lastName', 'primaryEmail'].includes(field) && (
                        <span className="text-[10px] font-bold uppercase tracking-tighter text-blue-500 bg-blue-100 px-1.5 py-0.5 rounded ml-2">
                          Core
                        </span>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default FieldConfiguration;
